import { AddressPencilButton } from 'components/forms/inputs/rhf-inputs/AddressInput';
import { RowChip } from 'components/GridItemList';
import { Warning } from 'components/icons';
import PersonaNonGrataChip from 'components/party/PersonaNonGrataChip';
import { RecordLink } from 'components/record-chip/RecordLink';
import { useIsSectionEditing } from 'components/section-edit/SectionEdit';
import { format, parseISO } from 'date-fns';
import React from 'react';
import { ContactDto, ContactMinDto, PartyToEmployeeRelationDto } from 'types/contacts';
import { ClientParty } from 'types/matters';
import { NamedEntityMinDto } from 'types/other';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { optionDisplayLabel } from 'utils/nameOrder';
import { partyNameString } from 'utils/partyNameUtil';

// `entity-link` is a targeting hook: a detail row colours it as a link at rest (index.css).
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

export const getRelations = (contact: ContactDto, types: string[]) => {
    if (!contact) return <div />;

    const employees = (contact?.managers || [])
        .concat(contact?.partners || [])
        .concat(contact?.administrators || [])
        .concat(contact?.businessContacts || [])
        .reduce(
            (result, relation) => {
                if (
                    types.includes(relation.partyEmployeeTypeCode) &&
                    relation.employee.name &&
                    !result.some((emp) => emp.id === relation.employee.id)
                ) {
                    result.push(relation.employee);
                }
                return result;
            },
            [] as PartyToEmployeeRelationDto['employee'][]
        )
        .sort((a, b) => a.name.localeCompare(b.name));

    const links = employees.map((employee, index) => (
        <React.Fragment key={employee.id}>
            {employee.deletedAt ? (
                <p className="text-fg text-caption flex leading-[1.8333] font-medium">
                    {`${employee.name} (${t('deleted')})`}
                    {index < employees.length - 1 && <span>,&nbsp;</span>}
                </p>
            ) : (
                <>
                    <RecordLink className={LINK_CLASS} to={`/employee/${employee.id}`} label={employee.name} />
                    {index < employees.length - 1 && <span>,&nbsp;</span>}
                </>
            )}
        </React.Fragment>
    ));

    return <div>{links}</div>;
};

/**
 * A multi-select's values as the chips of the new design's read row, in the order given: each the name its field's
 * chip shows, as the record's own chip unless the record is deleted, and a contact's persona non grata mark after it.
 */
export const linkChips = (
    path: 'employee' | 'contact' | 'group',
    items: { id: number; name: string; deletedAt?: string | null; isPersonaNonGrata?: boolean }[]
): RowChip[] =>
    items.map((item) => ({
        key: item.id,
        record: !item.deletedAt,
        label: item.deletedAt ? (
            item.name
        ) : (
            <RecordLink className={LINK_CLASS} to={`/${path}/${item.id}`} label={item.name} />
        ),
        adornment: item.isPersonaNonGrata ? <PersonaNonGrataChip /> : undefined,
    }));

/** {@link linkChips} for an employee multi-select's values, under the names its chips show. */
export const employeeChips = (employees: NamedEntityMinDto[], lastNameFirst: boolean) =>
    linkChips(
        'employee',
        employees.map((employee) => ({
            id: employee.id,
            name: optionDisplayLabel(employee, lastNameFirst, employee.name),
        }))
    );

export const renderWebsiteLink = (website: string | null | undefined, length = 300) => {
    if (!website || website === '-') return '-';

    const websitePattern =
        /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)/g;
    if (!websitePattern.test(website)) return website;

    const url = website.startsWith('http') ? website : `https://${website}`;
    const displayText = website.length > length ? `${website.substring(0, length)}…` : website;

    return (
        <a className={LINK_CLASS} href={url} target="_blank" rel="noopener noreferrer">
            {displayText}
        </a>
    );
};

const DAYS_IN_YEAR = 365;

export const calculateTimeSince = (
    lastEffortDate: string | null | undefined,
    contactType: string | null | undefined,
    dateFormat: string
) => {
    if (!lastEffortDate) return { text: t('time.never'), shouldHighlight: false };

    // parseISO keeps a date-only `yyyy-MM-dd` at local midnight; `new Date()` would read it as
    // UTC midnight and shift the calendar day for users west of UTC.
    const effortDate = parseISO(lastEffortDate);
    const now = new Date();
    const differenceInTime = now.getTime() - effortDate.getTime();
    const differenceInDays = differenceInTime / (1000 * 3600 * 24);

    if (differenceInDays < 1) {
        return { text: t('time.today'), shouldHighlight: false };
    } else if (differenceInDays <= 31) {
        return {
            text: t('time.days-ago', { count: Math.floor(differenceInDays).toString() }),
            shouldHighlight: false,
        };
    } else {
        const months = differenceInDays / 30;
        const shouldHighlight = (contactType === 'CLIENT' || contactType === 'LEAD') && months > 3;
        // Past a full year a month count stops being readable, so fall back to the actual date.
        return {
            text:
                differenceInDays >= DAYS_IN_YEAR
                    ? format(effortDate, dateFormat)
                    : t('time.months-ago', { count: Math.floor(months).toString() }),
            shouldHighlight,
        };
    }
};

/**
 * A list cell for {@link calculateTimeSince}'s text. An overdue one is red; the new design adds an icon, and a
 * screen reader hears the word, so the colour is never the only cue. The icon and its gap are small for the cell
 * to stay on one line: a "last effort" column at its narrowest leaves 118px, and "12 months ago" takes 100.
 */
export function TimeSinceCell({ text, overdue }: { text: string; overdue: boolean }) {
    return (
        <p
            className={cn(
                'text-body1',
                overdue && 'text-fg-error new:text-danger-ink new:flex new:items-center new:gap-0.5 new:font-medium'
            )}
        >
            {overdue ? <Warning className="new:block hidden size-3.5" /> : null}
            {text}
            {overdue ? <span className="sr-only"> {t('contacts.bd-effort-overdue')}</span> : null}
        </p>
    );
}

export const mapToContactMinDto = (contactDto: ContactDto | ClientParty): ContactMinDto => ({
    id: contactDto.id,
    name: contactDto.name,
    officialName: contactDto.officialName,
    firstName: 'firstName' in contactDto ? contactDto.firstName : undefined,
    lastName: 'lastName' in contactDto ? contactDto.lastName : undefined,
    mainEmail: contactDto.mainEmail,
    partyType: contactDto.partyType,
    relationshipTypeCode: contactDto.relationshipTypeCode,
    preferredLanguageCode: contactDto.preferredLanguageCode,
    deletedAt: contactDto.deletedAt,
    externalBillingInfo: contactDto.externalBillingInfo,
    invoicingDeadlineDay: contactDto.invoicingDeadlineDay,
    isPersonaNonGrata: contactDto.isPersonaNonGrata,
    isAddingTimecardsBlocked: contactDto.isAddingTimecardsBlocked,
    ...('partyType' in contactDto ? { partyType: contactDto.partyType } : {}),
    ...('isInternalClient' in contactDto ? { isInternalClient: contactDto.partyType } : {}),
});

export const getOptionLabelNameWithEmail = (contact: ContactMinDto, lastNameFirst = false) =>
    partyNameString(contact, { showEmail: true, lastNameFirst });

/**
 * The new design's Edit of one address on a read view: a pencil inline after the address. Its modal waits, as the
 * other cards' Edits do, while a section of the page edits: the pencil is then not to be seen or pressed, and
 * keeps its place, so the address keeps its lines.
 */
export const AddressEditPencil = ({ onClick }: { onClick: () => void }) => {
    const isWaiting = useIsSectionEditing();
    return (
        <AddressPencilButton
            aria-hidden={isWaiting || undefined}
            disabled={isWaiting}
            className={isWaiting ? 'invisible' : undefined}
            onClick={onClick}
        />
    );
};
